/**
 * JSON-LD for a listing page: Product (+ Offer or AggregateOffer) and BreadcrumbList.
 *
 * The rules, each one a lesson from the 2026-10 structured-data audit:
 *
 * - Product.name is the provider's title, never the <title> with " | cowork24" (Merchant listings
 *   showed the brand suffix as part of the product name on all 433 listings).
 * - Every URL is the slugless canonical (decision D1) — offers.url used to copy the address bar,
 *   tracking parameters included.
 * - Only what is true. A space booked through cowork24's own checkout gets an Offer sold by its
 *   provider. A lounge booked on LoungePair gets an AggregateOffer whose single Offer points at
 *   the partner and is sold by the partner: Google's merchant listings are for pages where the
 *   shopper buys, and these pages are not that.
 * - No shippingDetails, no hasMerchantReturnPolicy. Google lists them as recommended, but a desk,
 *   a meeting room or a lounge visit is a service booked for a date — it is never shipped and
 *   cannot be "returned". Claiming a shipping or return policy we do not have would be false
 *   markup; the validator warning is the honest outcome.
 * - aggregateRating/review only from real, public reviews loaded on the page, and never on
 *   affiliate listings (no ratings are imported from partners).
 */
import { findCityLandingSlug } from './cityLandingPageConfig';
import { htmlToPlainText } from './sanitize';
import { LISTING_STATE_CLOSED } from './types';
import {
  externalBookingUrlOf,
  isLoungeListing,
  listingCanonicalUrl,
  listingPriceUnit,
  listingTypeLabel,
  lowestListingPrice,
  parseListingAddress,
} from './listingSeo';

const SCHEMA_IN_STOCK = 'https://schema.org/InStock';
const SCHEMA_OUT_OF_STOCK = 'https://schema.org/OutOfStock';
const SCHEMA_NEW_CONDITION = 'https://schema.org/NewCondition';

// Google wants several large images; the -4x crop is 1600px wide and is already loaded by the
// listing page (ListingPage.duck.js). Six is plenty for a product.
const MAX_SCHEMA_IMAGES = 6;
const MAX_SCHEMA_REVIEWS = 5;

// UN/CEFACT codes for UnitPriceSpecification. Google ignores unit prices outside weight/volume,
// but the unit is a fact that AI answers and other consumers read ("CHF 45 per hour").
const UNIT_CODES = { hour: 'HUR', day: 'DAY', week: 'WEE', month: 'MON' };

// Partners whose booking page an affiliate Offer points at. The URL itself is already checked
// against EXTERNAL_BOOKING_HOSTS by externalBookingUrlOf.
const PARTNER_NAMES = { 'loungepair.com': 'LoungePair' };

const stripTrailingSlash = url => (url || '').replace(/\/+$/, '');

const schemaPrice = amountInSubunits => (amountInSubunits / 100).toFixed(2);

const partnerSeller = partnerUrl => {
  const { hostname, protocol } = new URL(partnerUrl);
  const host = Object.keys(PARTNER_NAMES).find(h => hostname === h || hostname.endsWith(`.${h}`));
  return {
    '@type': 'Organization',
    name: host ? PARTNER_NAMES[host] : hostname,
    url: `${protocol}//${hostname}/`,
  };
};

const listingSchemaImages = (listing, variantPrefix) => {
  const images = listing?.images || [];
  const large = images
    .map(image => {
      const variants = image?.attributes?.variants || {};
      const variant =
        variants[`${variantPrefix}-4x`] ||
        variants[`${variantPrefix}-2x`] ||
        variants[variantPrefix];
      return variant?.url;
    })
    .filter(Boolean)
    .slice(0, MAX_SCHEMA_IMAGES);
  // The 1200×630 social crop of the cover photo adds the wide aspect ratio Google asks for.
  const facebook = images[0]?.attributes?.variants?.facebook?.url;
  return [...new Set([...large, ...(facebook ? [facebook] : [])])];
};

const schemaAvailability = listing => {
  if (listing?.attributes?.state === LISTING_STATE_CLOSED) {
    return SCHEMA_OUT_OF_STOCK;
  }
  // Stock only exists on the purchase track. A day pass or a meeting room is sold against an
  // availability plan and has no `currentStock` at all — a published, open listing IS bookable.
  const stock = listing?.currentStock;
  if (!stock) {
    return SCHEMA_IN_STOCK;
  }
  return stock.attributes?.quantity > 0 ? SCHEMA_IN_STOCK : SCHEMA_OUT_OF_STOCK;
};

// A building value is a usable place name when it reads like one ("ORBIZ Josef – Coworking,
// 1. OG", "Haus Gryffenberg"); floor numbers ("4. Stock"), codes ("PK 230") and e-mail
// addresses (seen on prod) are not.
const isPlaceLikeBuilding = building =>
  typeof building === 'string' &&
  building.trim().length >= 12 &&
  /^[A-Za-zÀ-ÿ]/.test(building.trim()) &&
  !building.includes('@');

const placeOf = ({ listing, providerName, mapsConfig }) => {
  const publicData = listing?.attributes?.publicData || {};
  const { location = {}, countryCode } = publicData;
  const parsed = parseListingAddress(location.address);
  const addressCountry =
    (typeof countryCode === 'string' && countryCode.trim().toUpperCase()) || parsed.addressCountry;
  const address = { ...parsed, ...(addressCountry ? { addressCountry } : {}) };
  const hasAddress = Object.keys(address).length > 0;

  const geolocation = listing?.attributes?.geolocation;
  // With fuzzy locations on, the page itself hides the exact point; so does the markup.
  const showExactPoint =
    !mapsConfig?.fuzzy?.enabled &&
    Number.isFinite(geolocation?.lat) &&
    Number.isFinite(geolocation?.lng);

  const name = isPlaceLikeBuilding(location.building) ? location.building.trim() : providerName;
  if (!hasAddress && !showExactPoint) {
    return null;
  }
  return {
    '@type': 'Place',
    ...(name ? { name } : {}),
    ...(hasAddress ? { address: { '@type': 'PostalAddress', ...address } } : {}),
    ...(showExactPoint
      ? {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: geolocation.lat,
            longitude: geolocation.lng,
          },
        }
      : {}),
  };
};

const ratingSchemaMaybe = reviews => {
  const rated = (reviews || []).filter(review => {
    const rating = review?.attributes?.rating;
    return typeof rating === 'number' && rating > 0;
  });
  if (rated.length === 0) {
    return {};
  }
  const average = rated.reduce((sum, r) => sum + r.attributes.rating, 0) / rated.length;
  const reviewNodes = rated
    .filter(r => r?.author?.attributes?.profile?.displayName)
    .slice(0, MAX_SCHEMA_REVIEWS)
    .map(r => {
      const createdAt = r.attributes.createdAt;
      const date = createdAt instanceof Date ? createdAt : createdAt ? new Date(createdAt) : null;
      const hasDate = date && !Number.isNaN(date.getTime());
      return {
        '@type': 'Review',
        author: { '@type': 'Person', name: r.author.attributes.profile.displayName },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: r.attributes.rating,
          bestRating: 5,
          worstRating: 1,
        },
        ...(r.attributes.content ? { reviewBody: r.attributes.content } : {}),
        ...(hasDate ? { datePublished: date.toISOString().slice(0, 10) } : {}),
      };
    });
  return {
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: Math.round(average * 10) / 10,
      reviewCount: rated.length,
      bestRating: 5,
      worstRating: 1,
    },
    ...(reviewNodes.length > 0 ? { review: reviewNodes } : {}),
  };
};

/**
 * BreadcrumbList: Startseite › Coworking <City> › <title>, for lounges Startseite › Airport
 * Lounges › <title> (checked first: the Zurich-airport lounges lie inside the Zurich city box),
 * otherwise Startseite › <title>. The city is the same one SectionCityLinkMaybe links to, so the
 * declared path matches a visible link. The last item carries no URL (it is the page itself).
 *
 * @param {Object} params
 * @param {Object} params.listing listing entity
 * @param {Object} params.config app config (marketplaceRootURL)
 * @param {Object} params.intl react-intl
 * @returns {Object} BreadcrumbList node
 */
export const listingBreadcrumbSchema = ({ listing, config, intl }) => {
  const root = stripTrailingSlash(config.marketplaceRootURL);
  const title = (listing?.attributes?.title || '').trim();
  const crumbs = [
    { name: intl.formatMessage({ id: 'ListingPage.breadcrumbHome' }), item: `${root}/` },
  ];

  const citySlug = findCityLandingSlug(listing?.attributes?.geolocation);
  if (isLoungeListing(listing)) {
    crumbs.push({
      name: intl.formatMessage({ id: 'ListingPage.breadcrumbLounges' }),
      item: `${root}/airport-lounges`,
    });
  } else if (citySlug) {
    const city = intl.formatMessage({ id: `CityLandingPage.${citySlug}.placeName` });
    crumbs.push({
      name: intl.formatMessage({ id: 'ListingPage.breadcrumbCity' }, { city }),
      item: `${root}/coworking/${citySlug}`,
    });
  }
  crumbs.push({ name: title });

  return {
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      ...crumb,
    })),
  };
};

/**
 * Product node of a listing page.
 *
 * @param {Object} params
 * @param {Object} params.listing listing entity (with images, author, currentStock)
 * @param {Object} params.config app config (marketplaceRootURL, layout.listingImage, maps)
 * @param {Object} params.intl react-intl
 * @param {Array} [params.reviews] public reviews loaded for this listing
 * @returns {Object} Product node
 */
export const listingProductSchema = ({ listing, config, intl, reviews = [] }) => {
  const attributes = listing?.attributes || {};
  const uuid = listing?.id?.uuid;
  const canonicalUrl = listingCanonicalUrl(config.marketplaceRootURL, uuid);
  const root = stripTrailingSlash(config.marketplaceRootURL);
  const variantPrefix = config?.layout?.listingImage?.variantPrefix || 'listing-card';

  const providerName = (listing?.author?.attributes?.profile?.displayName || '').trim();
  const authorId = listing?.author?.id?.uuid;
  const partnerUrl = externalBookingUrlOf(listing);
  const lowest = lowestListingPrice(listing);
  const description = htmlToPlainText(attributes.description);
  const images = listingSchemaImages(listing, variantPrefix);
  const availability = schemaAvailability(listing);

  const priceMaybe = lowest
    ? { price: schemaPrice(lowest.amount), priceCurrency: lowest.currency }
    : {};

  let offers;
  if (partnerUrl) {
    // Booked and paid on the partner's site: cowork24 is an aggregator here, not the merchant.
    offers = {
      '@type': 'AggregateOffer',
      ...(lowest
        ? {
            lowPrice: schemaPrice(lowest.amount),
            highPrice: schemaPrice(lowest.amount),
            priceCurrency: lowest.currency,
          }
        : {}),
      offerCount: 1,
      availability,
      offers: {
        '@type': 'Offer',
        ...priceMaybe,
        url: partnerUrl,
        availability,
        seller: partnerSeller(partnerUrl),
      },
    };
  } else {
    const unit = lowest ? listingPriceUnit(listing, lowest.amount) : null;
    const unitCode = UNIT_CODES[unit];
    const place = placeOf({ listing, providerName, mapsConfig: config.maps });
    offers = {
      '@type': 'Offer',
      url: canonicalUrl,
      ...priceMaybe,
      ...(lowest && unitCode
        ? {
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              ...priceMaybe,
              unitCode,
            },
          }
        : {}),
      availability,
      itemCondition: SCHEMA_NEW_CONDITION,
      ...(providerName
        ? {
            seller: {
              '@type': 'Organization',
              name: providerName,
              ...(authorId ? { url: `${root}/u/${authorId}` } : {}),
            },
          }
        : {}),
      ...(place ? { availableAtOrFrom: place } : {}),
    };
  }

  return {
    '@type': 'Product',
    '@id': `${canonicalUrl}#product`,
    name: (attributes.title || '').trim(),
    ...(description ? { description } : {}),
    ...(uuid ? { sku: uuid } : {}),
    category: listingTypeLabel(listing, intl),
    // The provider is the brand of a space. Imported lounges belong to the cowork24 account and
    // are run by a lounge operator we do not know as an entity — no brand rather than a wrong one.
    ...(providerName && !partnerUrl ? { brand: { '@type': 'Brand', name: providerName } } : {}),
    ...(images.length > 0 ? { image: images } : {}),
    offers,
    ...(partnerUrl ? {} : ratingSchemaMaybe(reviews)),
  };
};

/**
 * Everything a listing page passes to <Page schema={…}>.
 *
 * @param {Object} params see listingProductSchema
 * @returns {Array<Object>} [Product, BreadcrumbList]
 */
export const listingSchemaGraph = params => [
  listingProductSchema(params),
  listingBreadcrumbSchema(params),
];
